<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  rule: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'save']);

const { t } = useI18n();
const saving = ref(false);
const showModal = ref(true);

const EVENTS = [
  { value: 'message_received', category: 'messages' },
  { value: 'message_sent', category: 'messages' },
  { value: 'first_response', category: 'messages' },
  { value: 'stage_changed', category: 'pipeline' },
  { value: 'card_created', category: 'pipeline' },
  { value: 'card_assigned', category: 'pipeline' },
  { value: 'label_added', category: 'labels' },
  { value: 'label_removed', category: 'labels' },
  { value: 'conversation_opened', category: 'conversation' },
  { value: 'conversation_resolved', category: 'conversation' },
  { value: 'conversation_reopened', category: 'conversation' },
  { value: 'contact_profile_updated', category: 'contact' },
  { value: 'contact_email_added', category: 'contact' },
  { value: 'contact_phone_added', category: 'contact' },
  { value: 'agent_assigned', category: 'agents' },
  { value: 'agent_replied', category: 'agents' },
];

const form = ref({
  event_type: '',
  name: '',
  description: '',
  points: 5,
  cooldown_minutes: 0,
  priority: 0,
  enabled: true,
});

const conditionsList = ref([]);

const isEditing = computed(() => !!props.rule);

const eventCategories = computed(() => [
  { id: 'messages', name: t('LEAD_SCORE.CATEGORIES.MESSAGES') },
  { id: 'pipeline', name: t('LEAD_SCORE.CATEGORIES.PIPELINE') },
  { id: 'labels', name: t('LEAD_SCORE.CATEGORIES.LABELS') },
  { id: 'conversation', name: t('LEAD_SCORE.CATEGORIES.CONVERSATION') },
  { id: 'contact', name: t('LEAD_SCORE.CATEGORIES.CONTACT') },
  { id: 'agents', name: t('LEAD_SCORE.CATEGORIES.AGENTS') },
]);

const getEventsForCategory = categoryId => {
  return EVENTS.filter(e => e.category === categoryId).map(e => ({
    value: e.value,
    label: t(`LEAD_SCORE.EVENTS.${e.value.toUpperCase()}`),
  }));
};

const incrementPoints = () => {
  if (form.value.points < 100) {
    form.value.points += 1;
  }
};

const decrementPoints = () => {
  if (form.value.points > -100) {
    form.value.points -= 1;
  }
};

const addCondition = () => {
  conditionsList.value.push({
    field: '',
    operator: 'eq',
    value: '',
  });
};

const removeCondition = index => {
  conditionsList.value.splice(index, 1);
};

const buildConditions = () => {
  const conditions = {};
  conditionsList.value.forEach(cond => {
    if (cond.field && cond.value) {
      conditions[cond.field] = {
        operator: cond.operator,
        value: cond.value,
      };
    }
  });
  return conditions;
};

const parseConditions = conditionsObj => {
  if (!conditionsObj) return [];
  return Object.entries(conditionsObj).map(([field, config]) => ({
    field,
    operator: config.operator || 'eq',
    value: config.value || '',
  }));
};

const handleSubmit = async () => {
  if (!form.value.event_type) return;

  saving.value = true;
  try {
    const ruleData = {
      ...form.value,
      conditions: buildConditions(),
    };
    emit('save', ruleData);
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  if (props.rule) {
    form.value = {
      event_type: props.rule.event_type || '',
      name: props.rule.name || '',
      description: props.rule.description || '',
      points: props.rule.points || 0,
      cooldown_minutes: props.rule.cooldown_minutes || 0,
      priority: props.rule.priority || 0,
      enabled: props.rule.enabled !== false,
    };
    conditionsList.value = parseConditions(props.rule.conditions);
  }
});
</script>

<template>
  <Modal v-model:show="showModal" size="medium" @close="$emit('close')">
    <div class="p-6 space-y-6">
      <h2 class="text-lg font-semibold text-n-slate-12">
        {{
          isEditing
            ? $t('LEAD_SCORE.EDITOR.EDIT_TITLE')
            : $t('LEAD_SCORE.EDITOR.CREATE_TITLE')
        }}
      </h2>
      <form class="space-y-6" @submit.prevent="handleSubmit">
        <!-- Event Type -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
            {{ $t('LEAD_SCORE.EDITOR.EVENT_TYPE') }}
          </label>
          <select
            v-model="form.event_type"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:ring-1 focus:ring-n-brand"
            required
          >
            <option value="" disabled>
              {{ $t('LEAD_SCORE.EDITOR.SELECT_EVENT') }}
            </option>
            <optgroup
              v-for="category in eventCategories"
              :key="category.id"
              :label="category.name"
            >
              <option
                v-for="event in getEventsForCategory(category.id)"
                :key="event.value"
                :value="event.value"
              >
                {{ event.label }}
              </option>
            </optgroup>
          </select>
        </div>

        <!-- Name -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
            {{ $t('LEAD_SCORE.EDITOR.NAME') }}
          </label>
          <input
            v-model="form.name"
            type="text"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-brand focus:ring-1 focus:ring-n-brand"
            :placeholder="$t('LEAD_SCORE.EDITOR.NAME_PLACEHOLDER')"
          />
        </div>

        <!-- Description -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
            {{ $t('LEAD_SCORE.EDITOR.DESCRIPTION') }}
          </label>
          <textarea
            v-model="form.description"
            rows="2"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-brand focus:ring-1 focus:ring-n-brand"
            :placeholder="$t('LEAD_SCORE.EDITOR.DESCRIPTION_PLACEHOLDER')"
          />
        </div>

        <!-- Points -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ $t('LEAD_SCORE.EDITOR.POINTS') }}
            </label>
            <div class="flex items-center gap-2">
              <button
                type="button"
                class="p-2 rounded-lg border border-n-weak hover:bg-n-alpha-2 transition-colors"
                @click="decrementPoints"
              >
                <span class="i-lucide-minus size-4 text-n-slate-11" />
              </button>
              <input
                v-model.number="form.points"
                type="number"
                class="flex-1 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-center text-n-slate-12 focus:border-n-brand focus:ring-1 focus:ring-n-brand"
                min="-100"
                max="100"
                required
              />
              <button
                type="button"
                class="p-2 rounded-lg border border-n-weak hover:bg-n-alpha-2 transition-colors"
                @click="incrementPoints"
              >
                <span class="i-lucide-plus size-4 text-n-slate-11" />
              </button>
            </div>
            <p class="text-xs text-n-slate-10 mt-1">
              {{ $t('LEAD_SCORE.EDITOR.POINTS_HINT') }}
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ $t('LEAD_SCORE.EDITOR.COOLDOWN') }}
            </label>
            <div class="flex items-center gap-2">
              <input
                v-model.number="form.cooldown_minutes"
                type="number"
                class="flex-1 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:ring-1 focus:ring-n-brand"
                min="0"
                max="10080"
              />
              <span class="text-sm text-n-slate-11">min</span>
            </div>
            <p class="text-xs text-n-slate-10 mt-1">
              {{ $t('LEAD_SCORE.EDITOR.COOLDOWN_HINT') }}
            </p>
          </div>
        </div>

        <!-- Priority -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
            {{ $t('LEAD_SCORE.EDITOR.PRIORITY') }}
          </label>
          <input
            v-model.number="form.priority"
            type="number"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:ring-1 focus:ring-n-brand"
            min="0"
            max="100"
          />
          <p class="text-xs text-n-slate-10 mt-1">
            {{ $t('LEAD_SCORE.EDITOR.PRIORITY_HINT') }}
          </p>
        </div>

        <!-- Conditions -->
        <div>
          <div class="flex items-center justify-between mb-2">
            <label class="block text-sm font-medium text-n-slate-12">
              {{ $t('LEAD_SCORE.EDITOR.CONDITIONS') }}
            </label>
            <button
              type="button"
              class="text-xs text-n-brand hover:text-n-brand/80 flex items-center gap-1"
              @click="addCondition"
            >
              <span class="i-lucide-plus size-3" />
              {{ $t('LEAD_SCORE.EDITOR.ADD_CONDITION') }}
            </button>
          </div>

          <div
            v-if="conditionsList.length === 0"
            class="text-sm text-n-slate-10 text-center py-4 border border-dashed border-n-weak rounded-lg"
          >
            {{ $t('LEAD_SCORE.EDITOR.NO_CONDITIONS') }}
          </div>

          <div v-else class="space-y-2">
            <div
              v-for="(condition, index) in conditionsList"
              :key="index"
              class="flex items-center gap-2 p-3 bg-n-alpha-1 rounded-lg"
            >
              <input
                v-model="condition.field"
                type="text"
                class="w-1/3 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-1.5 text-sm text-n-slate-12 focus:border-n-brand focus:ring-1 focus:ring-n-brand"
                :placeholder="$t('LEAD_SCORE.EDITOR.CONDITION_FIELD')"
              />
              <select
                v-model="condition.operator"
                class="w-1/4 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-1.5 text-sm text-n-slate-12 focus:border-n-brand focus:ring-1 focus:ring-n-brand"
              >
                <option value="eq">{{ $t('LEAD_SCORE.OPERATORS.EQ') }}</option>
                <option value="neq">
                  {{ $t('LEAD_SCORE.OPERATORS.NEQ') }}
                </option>
                <option value="gt">{{ $t('LEAD_SCORE.OPERATORS.GT') }}</option>
                <option value="gte">
                  {{ $t('LEAD_SCORE.OPERATORS.GTE') }}
                </option>
                <option value="lt">{{ $t('LEAD_SCORE.OPERATORS.LT') }}</option>
                <option value="lte">
                  {{ $t('LEAD_SCORE.OPERATORS.LTE') }}
                </option>
                <option value="contains">
                  {{ $t('LEAD_SCORE.OPERATORS.CONTAINS') }}
                </option>
                <option value="in">{{ $t('LEAD_SCORE.OPERATORS.IN') }}</option>
              </select>
              <input
                v-model="condition.value"
                type="text"
                class="flex-1 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-1.5 text-sm text-n-slate-12 focus:border-n-brand focus:ring-1 focus:ring-n-brand"
                :placeholder="$t('LEAD_SCORE.EDITOR.CONDITION_VALUE')"
              />
              <button
                type="button"
                class="p-1.5 rounded-lg hover:bg-n-alpha-2 text-ruby-500 transition-colors"
                @click="removeCondition(index)"
              >
                <span class="i-lucide-x size-4" />
              </button>
            </div>
          </div>
        </div>

        <!-- Enabled -->
        <div class="flex items-center gap-3">
          <label class="relative inline-flex cursor-pointer">
            <input
              v-model="form.enabled"
              type="checkbox"
              class="sr-only peer"
            />
            <div
              class="w-9 h-5 bg-n-alpha-3 peer-focus:ring-2 peer-focus:ring-n-brand rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-n-brand"
            />
          </label>
          <span class="text-sm text-n-slate-12">
            {{ $t('LEAD_SCORE.EDITOR.ENABLED') }}
          </span>
        </div>
      </form>
      <!-- Footer Actions -->
      <div class="flex justify-end gap-3 pt-4 border-t border-n-weak">
        <Button variant="outline" @click="$emit('close')">
          {{ $t('LEAD_SCORE.EDITOR.CANCEL') }}
        </Button>
        <Button solid blue :loading="saving" @click="handleSubmit">
          {{
            isEditing
              ? $t('LEAD_SCORE.EDITOR.UPDATE')
              : $t('LEAD_SCORE.EDITOR.CREATE')
          }}
        </Button>
      </div>
    </div>
  </Modal>
</template>
